import { useState, useEffect } from 'react';
import {
  getAssociateMembers,
  createAssociateMember,
  updateAssociateMember,
  deleteAssociateMember,
  getCurrentMembership,
} from '../services/api';
import Alert from '../components/common/Alert';

function AssociateMembers({ user }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [membershipType, setMembershipType] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    relationship: 'other',
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersData, membershipData] = await Promise.all([
        getAssociateMembers(),
        getCurrentMembership(),
      ]);
      const membersList = membersData.results || membersData;
      setMembers(membersList);
      setMembershipType(membershipData?.membership_type || null);
    } catch (err) {
      setError('Failed to load associate members');
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        email: member.email || '',
        date_of_birth: member.date_of_birth || '',
        relationship: member.relationship || 'other',
      });
    } else {
      setEditingMember(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        date_of_birth: '',
        relationship: 'other',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      date_of_birth: '',
      relationship: 'other',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Create payload, excluding empty email
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        relationship: formData.relationship,
      };

      if (formData.email) {
        payload.email = formData.email;
      }

      if (formData.date_of_birth) {
        payload.date_of_birth = formData.date_of_birth;
      }

      if (editingMember) {
        await updateAssociateMember(editingMember.id, payload);
        setSuccess('Associate member updated successfully');
      } else {
        await createAssociateMember(payload);
        setSuccess('Associate member added successfully');
      }

      handleCloseModal();
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save associate member');
      console.error('Failed to save associate member:', err);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this associate member?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await deleteAssociateMember(memberId);
      setSuccess('Associate member deleted successfully');
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete associate member');
      console.error('Failed to delete associate member:', err);
    }
  };

  const getRelationshipLabel = (relationship) => {
    const labels = {
      spouse: 'Spouse/Partner',
      child: 'Child',
      parent: 'Parent',
      sibling: 'Sibling',
      other: 'Other',
    };
    return labels[relationship] || relationship;
  };

  const formatBirthDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };


  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Associate Members</h1>
          <p className="mt-2 text-gray-600">
            Manage your family members and associates
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700"
        >
          Add Associate Member
        </button>
      </div>

      <Alert type="error" message={error} onClose={() => setError('')} />
      <Alert type="success" message={success} onClose={() => setSuccess('')} />

      {members.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-600 mb-4">You haven't added any associate members yet.</p>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Add Your First Associate Member
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => (
            <div key={member.id} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{member.full_name}</h3>
                  <p className="text-sm text-gray-600">{getRelationshipLabel(member.relationship)}</p>
                </div>
                {member.is_minor && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Minor
                  </span>
                )}
              </div>

              <div className="space-y-2 mb-4">
                {member.email && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Email:</span> {member.email}
                  </p>
                )}
                {member.date_of_birth && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Birth Date:</span> {formatBirthDate(member.date_of_birth)}
                  </p>
                )}
                {member.current_age !== null && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Age:</span> {member.current_age}
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={() => handleOpenModal(member)}
                  className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingMember ? 'Edit Associate Member' : 'Add Associate Member'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship *
                  </label>
                  <select
                    id="relationship"
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="spouse">Spouse/Partner</option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
                    Birth Month/Year (optional)
                  </label>
                  <input
                    type="month"
                    id="date_of_birth"
                    name="date_of_birth"
                    value={formData.date_of_birth ? formData.date_of_birth.substring(0, 7) : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        date_of_birth: value ? `${value}-01` : '',
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Used for age verification and age-restricted events
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                >
                  {editingMember ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssociateMembers;
